import type { ApiResponse } from "@/types/common";
import { apiClient, unwrapApiResponse } from "./httpClient";

export enum UploadFolder {
  Categories = 0,
  Products = 1,
  Stores = 2,
  Banners = 3,
}

export interface FileUploadResponseDto {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  contentType: string;
}

export function resolveUploadUrl(url: string): string {
  if (!url) return url;
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:") ||
    url.startsWith("data:")
  ) {
    return url;
  }

  const normalized = url.startsWith("/") ? url : `/${url}`;
  const apiBase = import.meta.env.VITE_API_URL ?? "http://localhost:5068/api/v1";

  // Production: API is same origin (/api/v1) — uploads live at /uploads/…
  if (apiBase.startsWith("/")) {
    return normalized;
  }

  const origin = apiBase.replace(/\/api\/v1\/?$/, "");
  return `${origin}${normalized}`;
}

export const filesService = {
  upload(file: File, folder: UploadFolder): Promise<FileUploadResponseDto> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", String(folder));

    return unwrapApiResponse(
      apiClient.post<ApiResponse<FileUploadResponseDto>>("/Files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    );
  },

  uploadMany(files: File[], folder: UploadFolder): Promise<FileUploadResponseDto[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("folder", String(folder));

    return unwrapApiResponse(
      apiClient.post<ApiResponse<FileUploadResponseDto[]>>("/Files/upload-many", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    );
  },

  delete(url: string): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.delete<ApiResponse<boolean>>("/Files", { data: { url } }),
    );
  },
};
